import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Check, Loader2, Lock, Pencil, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/library/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/library/command";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import CountryCodeCombobox from "@/components/common/inputs/CountryCodeCombobox";
import { SingleDateCalendar } from "@/components/ui/library/single-date-calendar";
import { sanitizeCfNumberInput } from "@/features/customField/utils/cfValueTypes";
import type { RequiredFieldMark } from "@/features/leads/requirements/requiredFieldMarks";
import { RequiredStar } from "@/features/leads/requirements/requiredFieldMarks.ui";

/**
 * IN-PLACE field editing for the lead drawer's Overview cards.
 *
 * Design contract (this is the whole point of the component — don't break it):
 *   1. VIEW MODE IS PIXEL-IDENTICAL to `DetailItem`. Same text, same classes.
 *      Dropping this in place of a `DetailItem` must not move a single pixel.
 *   2. NOTHING IN THE DOCUMENT FLOW EVER CHANGES SIZE. The pencil lives in a
 *      zero-size span pinned right after the label text; the editor is either
 *      an absolute overlay on top of the value line (text / phone) or a
 *      portalled popover hanging off the pencil (select / multi-select /
 *      long text). The value paragraph keeps occupying its exact box the
 *      whole time (`invisible`, never removed), so neighbouring cells, the
 *      card, and the drawer never reflow.
 *   3. NO GLOBAL EDIT MODE. Each field saves itself with a PATCH carrying
 *      only that field — the drawer never flips into the full edit form.
 *
 * Keyboard: Enter saves, Escape cancels. Clicking outside an open text
 * editor saves when the value changed (clicks inside a portalled popover —
 * e.g. the country-code picker — don't count as outside).
 *
 * The popover editors are driven by a real `PopoverTrigger` (the pencil) with
 * a state-controlled `open`, matching `CountryCodeCombobox`. Mounting a
 * Radix popover that is ALREADY open, or handing its anchor a competing ref,
 * leaves the popper unpositioned — Radix then parks the panel at
 * `translate(0, -200%)` and the click looks like it did nothing.
 */

const ACCENT = "#267355";

export type InlineOption = { value: string; label: string };

export type InlineEditorSpec =
  | {
      kind: "text";
      value: string;
      placeholder?: string;
      type?: "text" | "email" | "tel";
      /** Digits-only input (WhatsApp / phone-ish fields). */
      numeric?: boolean;
      /** Number / Percentage custom fields: digits, one point and — when
       *  "signed" — a leading minus. */
      decimal?: "signed" | "unsigned";
    }
  | { kind: "textarea"; value: string; placeholder?: string }
  | {
      kind: "select";
      value: string;
      options: InlineOption[];
      placeholder?: string;
      /** Offer a "Clear" row that saves an empty value. */
      clearable?: boolean;
    }
  | {
      kind: "multiselect";
      value: string[];
      options: InlineOption[];
      placeholder?: string;
    }
  | {
      kind: "phone";
      value: { countryCode: string; national: string };
      /** Number input hint (default "Mobile number"; WhatsApp passes its own). */
      placeholder?: string;
    }
  | {
      /** Calendar-day picker. Value is "YYYY-MM-DD" (local); commits on pick. */
      kind: "date";
      value: string;
      /** "YYYY-MM-DD" — later days are not selectable. */
      maxDate?: string;
      /** Month + year dropdowns (dates far from today, e.g. a birth date). */
      yearPicker?: boolean;
      /** A "Clear date" button under the calendar that saves "". */
      clearable?: boolean;
    };

interface InlineEditableDetailProps {
  label: string;
  /** Formatted display value — same thing `DetailItem` would render. */
  value: ReactNode;
  /** Omit to render a plain, non-editable detail (no pencil). */
  editor?: InlineEditorSpec;
  /** Receives the new value; reject/throw to keep the editor open. */
  onSave?: (next: any) => Promise<unknown> | unknown;
  /** Hides the pencil entirely (no permission / nothing to edit). */
  readOnly?: boolean;
  /** Shows a lock instead of the pencil, with this text as the tooltip. */
  lockedReason?: string;
  /**
   * Stage-requirement star (see `requiredFieldMarks.tsx`). Rendered inside
   * the label's own text run, so it costs the layout nothing — contract #2.
   */
  required?: RequiredFieldMark | null;
  className?: string;
}

/** Popover-based editors — anything that needs more room than one line. */
const POPOVER_KINDS = new Set(["select", "multiselect", "textarea", "date"]);

/** Shared pencil styling — sits in a zero-size box, so it never reflows. */
const PENCIL_CLASS =
  "absolute left-[5px] top-1/2 -translate-y-1/2 z-20 grid size-[17px] cursor-pointer place-items-center rounded-[5px] border border-[#e6e8e7] bg-white text-[var(--color-black-6)] opacity-0 shadow-[0_1px_2px_rgba(5,25,18,0.06)] transition-[opacity,color,border-color] duration-150 hover:border-[#267355]/50 hover:text-[#267355] focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#267355]/30 group-hover/inline:opacity-100 data-[state=open]:opacity-100 data-[state=open]:border-[#267355] data-[state=open]:text-[#267355]";

function initialDraft(editor: InlineEditorSpec): any {
  switch (editor.kind) {
    case "multiselect":
      return [...editor.value];
    case "phone":
      return { ...editor.value };
    default:
      return editor.value ?? "";
  }
}

function isUnchanged(editor: InlineEditorSpec, draft: any): boolean {
  switch (editor.kind) {
    case "multiselect": {
      const a = [...(editor.value ?? [])].sort();
      const b = [...(draft ?? [])].sort();
      return a.length === b.length && a.every((v, i) => v === b[i]);
    }
    case "phone":
      return (
        (editor.value?.countryCode ?? "") === (draft?.countryCode ?? "") &&
        (editor.value?.national ?? "") === (draft?.national ?? "")
      );
    default:
      return (editor.value ?? "") === (draft ?? "");
  }
}

export default function InlineEditableDetail({
  label,
  required,
  value,
  editor,
  onSave,
  readOnly,
  lockedReason,
  className,
}: InlineEditableDetailProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<any>("");
  const [isSaving, setIsSaving] = useState(false);

  const overlayRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLInputElement | null>(null);

  const editable = Boolean(editor) && !readOnly && !lockedReason;
  const usesPopover = editor ? POPOVER_KINDS.has(editor.kind) : false;
  const isOverlayEditing = isEditing && !usesPopover;
  const isPopoverEditing = isEditing && usesPopover;
  /** Confirm/discard only appear once something actually changed. */
  const isDirty = Boolean(editor) && !isUnchanged(editor!, draft);

  const displayValue = value === "" || value == null ? "N/A" : value;
  const titleValue =
    typeof displayValue === "string" || typeof displayValue === "number"
      ? String(displayValue)
      : undefined;

  const closeEditor = useCallback(() => {
    setIsEditing(false);
    setIsSaving(false);
  }, []);

  const startEdit = useCallback(() => {
    if (!editor || !editable) return;
    setDraft(initialDraft(editor));
    setIsEditing(true);
  }, [editor, editable]);

  const commit = useCallback(
    async (next: any) => {
      if (!editor) return;
      // A save already in flight owns this edit — a stray outside-click or a
      // second ✓ must not fire the same PATCH twice.
      if (isSaving) return;
      if (!onSave || isUnchanged(editor, next)) {
        closeEditor();
        return;
      }
      setIsSaving(true);
      try {
        await onSave(next);
        closeEditor();
      } catch {
        // Mutation hooks already toast the failure — keep the editor open
        // (with the user's input intact) so they can retry or cancel.
        setIsSaving(false);
      }
    },
    [editor, onSave, closeEditor, isSaving],
  );

  // Focus the overlay input the moment it mounts.
  useEffect(() => {
    if (!isOverlayEditing) return;
    const id = window.requestAnimationFrame(() => {
      fieldRef.current?.focus();
      fieldRef.current?.select?.();
    });
    return () => window.cancelAnimationFrame(id);
  }, [isOverlayEditing]);

  // Outside-click on an open overlay editor commits (Escape / ✕ cancels).
  // Clicks landing inside a portalled Radix popover (the country-code
  // picker) are NOT outside — they're part of this editor.
  useEffect(() => {
    if (!isOverlayEditing) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (!target) return;
      if (overlayRef.current?.contains(target)) return;
      if (target.closest?.("[data-radix-popper-content-wrapper]")) return;
      void commit(draft);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [isOverlayEditing, commit, draft]);

  /** Pencil / lock, pinned just after the label text in a zero-size box. */
  const affordance = (() => {
    if (lockedReason) {
      return (
        <Tooltip delayDuration={150}>
          <TooltipTrigger asChild>
            <span
              className="absolute left-[5px] top-1/2 z-20 grid size-[17px] -translate-y-1/2 place-items-center rounded-[5px] bg-white/90 text-[var(--color-black-5)] opacity-0 transition-opacity duration-150 group-hover/inline:opacity-100"
              aria-label={`${label} is locked`}
            >
              <Lock className="size-[10px]" strokeWidth={2} />
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[240px] text-xs leading-snug">
            {lockedReason}
          </TooltipContent>
        </Tooltip>
      );
    }

    if (!editable || !editor) return null;

    // Text / phone: the pencil just swaps the value line for the overlay.
    if (!usesPopover) {
      if (isEditing) return null;
      return (
        <button
          type="button"
          onClick={startEdit}
          aria-label={`Edit ${label}`}
          title={`Edit ${label}`}
          className={PENCIL_CLASS}
        >
          <Pencil className="size-[10px]" strokeWidth={2} />
        </button>
      );
    }

    // Select / multi-select / remark: the pencil IS the popover trigger, so
    // Radix owns open/close/positioning (same shape as CountryCodeCombobox).
    return (
      <Popover
        open={isEditing}
        onOpenChange={(open) => {
          if (open) {
            startEdit();
            return;
          }
          // Single-select and date already committed on pick; the staged
          // editors (multi-select / remark) commit whatever is in the draft.
          if (editor.kind === "select" || editor.kind === "date") closeEditor();
          else void commit(draft);
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Edit ${label}`}
            title={`Edit ${label}`}
            className={PENCIL_CLASS}
          >
            <Pencil className="size-[10px]" strokeWidth={2} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={6}
          className={cn(
            "overflow-hidden rounded-[10px] border-[#e6e8e7] p-0 shadow-[0_8px_24px_rgba(5,25,18,0.12)]",
            // The calendar grid is seven fixed columns — see the note in
            // `single-date-picker.tsx` on why it must not be elastic.
            editor.kind === "date" ? "w-[288px] p-4" : "w-[240px]",
          )}
          onOpenAutoFocus={(e) => {
            if (editor.kind === "textarea") return;
            e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            // Escape must always CANCEL — without this it falls through to
            // `onOpenChange`, which commits for the staged editors.
            e.preventDefault();
            closeEditor();
          }}
        >
          {editor.kind === "textarea" ? (
            <TextareaEditor
              draft={draft}
              setDraft={setDraft}
              placeholder={editor.placeholder}
              isSaving={isSaving}
              onCancel={closeEditor}
              onSave={() => void commit(draft)}
            />
          ) : editor.kind === "multiselect" ? (
            <MultiSelectEditor
              label={label}
              options={editor.options}
              draft={draft as string[]}
              setDraft={setDraft}
              isSaving={isSaving}
              onDone={() => void commit(draft)}
            />
          ) : editor.kind === "select" ? (
            <SelectEditor
              label={label}
              options={editor.options}
              current={draft as string}
              clearable={editor.clearable}
              isSaving={isSaving}
              onPick={(next) => void commit(next)}
            />
          ) : editor.kind === "date" ? (
            <div className={cn(isSaving && "pointer-events-none opacity-60")}>
              <SingleDateCalendar
                date={draft as string}
                maxDate={editor.maxDate}
                yearPicker={editor.yearPicker}
                onDateChange={(next) => void commit(next)}
              />
              {editor.clearable && Boolean(draft) && (
                <button
                  type="button"
                  onClick={() => void commit("")}
                  className="mt-2 text-[12px] font-medium text-[#697571] hover:text-[#da2a46]"
                >
                  Clear date
                </button>
              )}
            </div>
          ) : null}
        </PopoverContent>
      </Popover>
    );
  })();

  return (
    <div
      className={cn(
        "group/inline relative flex flex-col gap-0 min-w-0",
        className,
      )}
    >
      {/* Label row. The pencil hangs in a zero-size span immediately after
          the label text, so it reads as part of THIS field and still costs
          the layout nothing. */}
      <div className="flex min-w-0 items-center">
        <p className="min-w-0 truncate text-[12px] text-[var(--color-black-6)] tracking-[-0.24px]">
          {label}
          <RequiredStar mark={required} />
        </p>
        <span className="relative h-0 w-0 shrink-0">{affordance}</span>
      </div>

      <div className="relative min-w-0">
        <p
          className={cn(
            "text-[14px] font-medium text-[var(--color-black-10)] tracking-[-0.28px] break-words",
            isOverlayEditing && "invisible",
            // While a popover editor is open the value stays visible inside
            // the highlight box, so give it the same 7px inset the text
            // overlay uses — both editing states then share one geometry.
            isPopoverEditing && "pl-[7px]",
          )}
          title={titleValue}
        >
          {displayValue}
        </p>

        {/* Marks which field the open popover belongs to. Absolute → free.
            Same box the text overlay occupies (26px, centred) so the two
            editing states read as one language; grows to cover a value
            that wrapped onto more lines. */}
        {isPopoverEditing && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-1/2 h-[max(22px,100%)] -translate-y-1/2 rounded-[5px] border border-[#267355]/45 bg-[#267355]/[0.05]"
          />
        )}

        {editor && isOverlayEditing && editor.kind === "text" && (
          <OverlayShell ref={overlayRef} isSaving={isSaving}>
            <input
              ref={fieldRef}
              type={editor.type ?? "text"}
              inputMode={
                editor.numeric ? "numeric" : editor.decimal ? "decimal" : undefined
              }
              value={draft as string}
              placeholder={editor.placeholder}
              disabled={isSaving}
              onChange={(e) =>
                setDraft(
                  editor.numeric
                    ? e.target.value.replace(/[^\d]/g, "")
                    : editor.decimal
                      ? sanitizeCfNumberInput(
                          e.target.value,
                          editor.decimal === "signed",
                        )
                      : e.target.value,
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void commit(draft);
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  closeEditor();
                }
              }}
              className="h-full min-w-0 flex-1 bg-transparent px-0 text-[14px] font-medium text-[var(--color-black-10)] tracking-[-0.28px] leading-none outline-none placeholder:font-normal placeholder:text-[var(--color-black-5)]"
            />
            <EditorActions
              isSaving={isSaving}
              isDirty={isDirty}
              onCancel={closeEditor}
              onSave={() => void commit(draft)}
            />
          </OverlayShell>
        )}

        {editor && isOverlayEditing && editor.kind === "phone" && (
          <OverlayShell ref={overlayRef} isSaving={isSaving} minWidth={216}>
            <CountryCodeCombobox
              value={draft?.countryCode}
              onChange={(code) =>
                setDraft((prev: any) => ({ ...prev, countryCode: code }))
              }
              disabled={isSaving}
              triggerClassName="h-[18px] w-[58px] shrink-0 gap-0.5 rounded-[4px] border-[#cdd1d0] bg-white px-1 text-[11px] font-medium [&>svg]:size-2.5"
            />
            <input
              ref={fieldRef}
              type="tel"
              inputMode="numeric"
              value={draft?.national ?? ""}
              disabled={isSaving}
              placeholder={editor.placeholder ?? "Mobile number"}
              onChange={(e) =>
                setDraft((prev: any) => ({
                  ...prev,
                  national: e.target.value.replace(/[^\d]/g, ""),
                }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void commit(draft);
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  closeEditor();
                }
              }}
              className="h-full min-w-0 flex-1 bg-transparent px-0 text-[14px] font-medium text-[var(--color-black-10)] tracking-[-0.28px] leading-none outline-none placeholder:font-normal placeholder:text-[var(--color-black-5)]"
            />
            <EditorActions
              isSaving={isSaving}
              isDirty={isDirty}
              onCancel={closeEditor}
              onSave={() => void commit(draft)}
            />
          </OverlayShell>
        )}
      </div>
    </div>
  );
}

/**
 * The absolute overlay that sits exactly on top of the value line.
 *
 * Geometry is deliberate, not decorative:
 *   - `inset-x-0` — the box spans EXACTLY its own grid cell, so its left edge
 *     sits on the same vertical line as the label above it and as every other
 *     column in the card. (An earlier version bled 7px past the cell to keep
 *     the text from shifting; that traded a 7px text nudge nobody notices for
 *     a broken gutter everybody does.)
 *   - `top-1/2 -translate-y-1/2` centres it on the value line, whatever that
 *     line box measures.
 *   - `h-[22px]` against a 21px line box. The cell is only 39px tall (18px
 *     label line + 21px value line), so height is not free: a 24px box
 *     centred here leaves ~1px under the label's descenders, which is what
 *     made it feel cramped. 22px clears them by ~2.5px and still wraps 14px
 *     text with 2.5px to spare — tight and chip-like, the way an inline
 *     editor should read, not a form field dropped into a compact grid.
 *   - Tinted fill + a light accent border, NOT a white box with a hard
 *     outline: this should look like the cell woke up, not like a form
 *     control landed on it.
 * `minWidth` is opt-in and only used where the control genuinely needs it
 * (the phone editor's country picker); with `left` pinned it can only ever
 * grow rightwards, never back across the gutter.
 */
function OverlayShell({
  children,
  isSaving,
  minWidth,
  ref,
}: {
  children: ReactNode;
  isSaving: boolean;
  minWidth?: number;
  ref?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      ref={ref}
      style={minWidth ? { minWidth } : undefined}
      className={cn(
        "absolute inset-x-0 top-1/2 z-30 flex h-[22px] -translate-y-1/2 items-center gap-1 rounded-[5px] border pl-[7px] pr-[3px] shadow-[0_1px_3px_rgba(5,25,18,0.07)] transition-colors",
        isSaving
          ? "border-[#cdd1d0] bg-white"
          : "border-[#267355]/45 bg-[#f5faf8]",
      )}
    >
      {children}
    </div>
  );
}

/**
 * Confirm / discard, shown ONLY once the value actually changed — an
 * untouched editor stays a clean single line (Esc or clicking away closes
 * it). Mirrors the click-to-edit name field in the drawer header.
 */
function EditorActions({
  isSaving,
  isDirty,
  onCancel,
  onSave,
}: {
  isSaving: boolean;
  isDirty: boolean;
  onCancel: () => void;
  onSave: () => void;
}) {
  if (isSaving) {
    return (
      <Loader2
        className="size-[13px] shrink-0 animate-spin text-[var(--color-black-6)]"
        strokeWidth={2}
      />
    );
  }
  if (!isDirty) return null;
  return (
    <span className="flex shrink-0 items-center gap-[2px]">
      <button
        type="button"
        onClick={onCancel}
        aria-label="Discard"
        className="grid size-[16px] cursor-pointer place-items-center rounded-[4px] text-[var(--color-black-5)] transition-colors hover:bg-[#e8efec] hover:text-[var(--color-black-8)]"
      >
        <X className="size-[10px]" strokeWidth={2.5} />
      </button>
      <button
        type="button"
        onClick={onSave}
        aria-label="Save"
        className="grid size-[16px] cursor-pointer place-items-center rounded-[4px] bg-[#267355] text-white transition-colors hover:bg-[#1e5c44]"
      >
        <Check className="size-[10px]" strokeWidth={2.75} />
      </button>
    </span>
  );
}

function SelectEditor({
  label,
  options,
  current,
  clearable,
  isSaving,
  onPick,
}: {
  label: string;
  options: InlineOption[];
  current: string;
  clearable?: boolean;
  isSaving: boolean;
  onPick: (next: string) => void;
}) {
  const searchable = options.length > 7;
  return (
    <Command>
      {searchable && (
        <CommandInput placeholder={`Search ${label.toLowerCase()}…`} />
      )}
      <CommandList className="max-h-[240px]">
        <CommandEmpty>No options available.</CommandEmpty>
        {clearable && current && (
          <CommandItem
            value="__clear__"
            disabled={isSaving}
            onSelect={() => onPick("")}
            className="text-[13px] text-[var(--color-black-6)]"
          >
            Clear
          </CommandItem>
        )}
        {options.map((option) => (
          <CommandItem
            key={option.value}
            value={`${option.label} ${option.value}`}
            disabled={isSaving}
            onSelect={() => onPick(option.value)}
            className="flex items-center justify-between gap-2 text-[13px]"
          >
            <span className="truncate">{option.label}</span>
            {option.value === current && (
              <Check
                className="size-[13px] shrink-0"
                style={{ color: ACCENT }}
                strokeWidth={2.5}
              />
            )}
          </CommandItem>
        ))}
      </CommandList>
    </Command>
  );
}

function MultiSelectEditor({
  label,
  options,
  draft,
  setDraft,
  isSaving,
  onDone,
}: {
  label: string;
  options: InlineOption[];
  draft: string[];
  setDraft: (updater: any) => void;
  isSaving: boolean;
  onDone: () => void;
}) {
  const selected = new Set(draft ?? []);
  const toggle = (value: string) =>
    setDraft((prev: string[]) =>
      prev?.includes(value)
        ? prev.filter((v) => v !== value)
        : [...(prev ?? []), value],
    );

  return (
    <div className="flex flex-col">
      <Command>
        {options.length > 7 && (
          <CommandInput placeholder={`Search ${label.toLowerCase()}…`} />
        )}
        <CommandList className="max-h-[220px]">
          <CommandEmpty>No options available.</CommandEmpty>
          {options.map((option) => {
            const isOn = selected.has(option.value);
            return (
              <CommandItem
                key={option.value}
                value={`${option.label} ${option.value}`}
                disabled={isSaving}
                onSelect={() => toggle(option.value)}
                className="flex items-center gap-2 text-[13px]"
              >
                <span
                  className={cn(
                    "grid size-[14px] shrink-0 place-items-center rounded-[4px] border",
                    isOn
                      ? "border-[#267355] bg-[#267355] text-white"
                      : "border-[#cdd1d0]",
                  )}
                >
                  {isOn && <Check className="size-[9px]" strokeWidth={3} />}
                </span>
                <span className="truncate">{option.label}</span>
              </CommandItem>
            );
          })}
        </CommandList>
      </Command>
      <div className="flex items-center justify-between gap-2 border-t border-[#e6e8e7] px-2 py-1.5">
        <button
          type="button"
          disabled={isSaving}
          onClick={() => setDraft([])}
          className="cursor-pointer text-[12px] text-[var(--color-black-6)] transition-colors hover:text-[var(--color-black-8)]"
        >
          Clear all
        </button>
        <button
          type="button"
          disabled={isSaving}
          onClick={onDone}
          className="flex cursor-pointer items-center gap-1 rounded-[6px] bg-[#267355] px-2.5 py-1 text-[12px] font-medium text-white transition-colors hover:bg-[#1e5c44]"
        >
          {isSaving && <Loader2 className="size-3 animate-spin" />}
          Done
        </button>
      </div>
    </div>
  );
}

function TextareaEditor({
  draft,
  setDraft,
  placeholder,
  isSaving,
  onCancel,
  onSave,
}: {
  draft: string;
  setDraft: (value: string) => void;
  placeholder?: string;
  isSaving: boolean;
  onCancel: () => void;
  onSave: () => void;
}) {
  return (
    <div className="flex flex-col gap-2 p-2">
      <textarea
        autoFocus
        rows={4}
        value={draft ?? ""}
        placeholder={placeholder}
        disabled={isSaving}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            onSave();
          }
          if (e.key === "Escape") {
            e.preventDefault();
            onCancel();
          }
        }}
        className="w-full resize-none rounded-[8px] border border-[#e6e8e7] bg-[#fcfcfc] px-2 py-1.5 text-[13px] text-[var(--color-black-9)] outline-none focus:border-[#267355]"
      />
      <div className="flex items-center justify-end gap-1.5">
        <button
          type="button"
          disabled={isSaving}
          onClick={onCancel}
          className="cursor-pointer rounded-[6px] px-2 py-1 text-[12px] text-[var(--color-black-6)] transition-colors hover:bg-[#f4f6f5]"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={isSaving}
          onClick={onSave}
          className="flex cursor-pointer items-center gap-1 rounded-[6px] bg-[#267355] px-2.5 py-1 text-[12px] font-medium text-white transition-colors hover:bg-[#1e5c44]"
        >
          {isSaving && <Loader2 className="size-3 animate-spin" />}
          Save
        </button>
      </div>
    </div>
  );
}
